<script>
import { each, isEqual, isArray } from 'lodash-es'
import L from 'leaflet'
import { PruneCluster, PruneClusterForLeaflet } from 'prunecluster'

import 'leaflet/dist/leaflet.css'
import 'drmonty-leaflet-awesome-markers/css/leaflet.awesome-markers.css'
import 'drmonty-leaflet-awesome-markers'

L.Icon.MarkerCluster = L.Icon.extend({
  options: {
    iconSize: new L.Point(44, 44),
    className: 'prunecluster leaflet-markercluster-icon'
  },

  createIcon: function () {
    const e = document.createElement('canvas')
    this._setIconStyles(e, 'icon')
    const s = this.options.iconSize
    e.width = s.x
    e.height = s.y
    this.draw(e.getContext('2d'), s.x, s.y)
    return e
  },

  createShadow: function () {
    return null
  },

  draw: function (canvas) {
    const cluster = this
    // Cluster colors (0: unknown, 1: connected, 2: disconnected)
    const colors = ['lightgray', 'green', 'red']

    let start = 0
    each(colors, (color, i) => {
      const size = cluster.stats[i] / cluster.population

      if (size > 0) {
        canvas.beginPath()
        canvas.moveTo(22, 22)
        canvas.fillStyle = color
        let from = start + 0.14
        const to = start + size * Math.PI * 2

        if (to < from) {
          from = start
        }
        canvas.arc(22, 22, 22, from, to)

        start = start + size * Math.PI * 2
        canvas.lineTo(22, 22)
        canvas.fill()
        canvas.closePath()
      }
    })

    canvas.beginPath()
    canvas.fillStyle = 'white'
    canvas.arc(22, 22, 18, 0, Math.PI * 2)
    canvas.fill()
    canvas.closePath()

    canvas.fillStyle = '#555'
    canvas.textAlign = 'center'
    canvas.textBaseline = 'middle'
    canvas.font = 'bold 12px sans-serif'

    canvas.fillText(this.population, 22, 22, 40)
  }
})

export default {
  props: {
    // List of object that will be displayed on map
    markers: {
      type: Array,
      required: true,
      default: () => []
    },
    // Display popup on click?
    popup: {
      type: Boolean,
      default: false
    },
    // Popup content, use $0, $1...$n to display data attribute
    popupContent: {
      type: String,
      default: ''
    },
    // Array of data attributes to display in popupContent
    popupData: {
      type: Array,
      default: () => []
    },
    // Font awesome icons
    icon: {
      type: String,
      required: true
    },
    iconPrefix: {
      type: String,
      default: 'icon'
    },
    iconColor: {
      type: String,
      default: 'white'
    },
    markerColor: {
      type: Function,
      default: () => 'black'
    },
    options: {
      type: Object,
      default: () => ({})
    }
  },

  watch: {
    markers (newMarkers, oldMarkers) {
      if (!isEqual(newMarkers, oldMarkers)) {
        this.createCluster()
      }
    }
  },

  created () {
    this.createCluster()
  },

  methods: {
    deferredMountedTo (parent) {
      this.parent = parent
      for (var i = 0; i < this.$children.length; i++) {
        this.$children[i].deferredMountedTo(this.mapObject)
      }

      this.mapObject.addTo(parent)
    },

    createCluster () {
      if (this.mapObject) {
        this.mapObject.remove()
      }

      this.mapObject = new PruneClusterForLeaflet()

      this.mapObject.BuildLeafletClusterIcon = function (cluster) {
        const e = new L.Icon.MarkerCluster()
        e.stats = cluster.stats
        e.population = cluster.population
        return e
      }

      each(this.markers, (marker, i) => {
        // Stop if no coordinates
        if (marker.coordinates) {
          if (isArray(marker.coordinates) && (marker.coordinates[0] === null || marker.coordinates[1] === null)) return
          if (!isArray(marker.coordinates) && (marker.coordinates.lat === null || marker.coordinates.lng === null)) return
        }
        if (!marker.coordinates && !marker.latitude && !marker.longitude) return

        // Get lat and long
        // GeoJson is [lng, lat, alt]
        const lng = marker.longitude || marker.coordinates.lng || marker.coordinates[0]
        const lat = marker.latitude || marker.coordinates.lat || marker.coordinates[1]

        const pruneMarker = new PruneCluster.Marker(lat, lng)
        pruneMarker.category = marker.category
        pruneMarker.data.icon = new L.AwesomeMarkers.Icon({
          icon: this.icon,
          prefix: this.iconPrefix,
          markerColor: this.markerColor(marker),
          iconColor: this.iconColor
        })

        // Popup
        if (this.popup) {
          let parsedContent = this.popupContent

          each(this.popupData, (attr, index) => {
            if (typeof attr === 'object') {
              // Passing an object attr/[index]/formatter to format data
              let attrValue = marker[attr.attr]

              // In case of an array, pass index number to get the correct value
              if (attr.index !== undefined) {
                attrValue = attrValue[attr.index]
              }

              // In case we need to format data, pass a formatter function
              if (attr.formatter) {
                attrValue = attr.formatter(attrValue)
              }

              // Then replace it in popup
              parsedContent = this.$utils.replaceAll(parsedContent, '$' + index, attrValue)
            } else {
              // Passing only a string attr to get raw data
              parsedContent = this.$utils.replaceAll(parsedContent, '$' + index, marker[attr])
            }
          })

          pruneMarker.data.popup = parsedContent
        }

        this.mapObject.RegisterMarker(pruneMarker)
      })

      if (this.$parent._isMounted) {
        this.deferredMountedTo(this.$parent.mapObject)
      }
    }
  },

  render () {
    return null
  }
}
</script>
